// A generated click track that plays ALONGSIDE a file's own audio (rather
// than instead of it), for a song whose file has no click baked in. Routed
// per the active setlist's routing choice (see PlaybackController.
// resolveOutputSides) — 'left'/'right' for a genuine PA/in-ear split, or
// 'stereo' so the click is audible in both ears while the track stays
// untouched (the rehearsal case) — using the same explicit splitter/merger
// routing used everywhere else in this app. Anchored to FILE-time position
// via AudioContext.currentTime, not wall-clock playback start — the song's
// real downbeat essentially never lands exactly on 0.000s, so an
// `offsetSeconds` shifts the click's own beat-1 to line up with it (tuned
// by ear in the Song Library).
//
// This is a lookahead scheduler (schedule audio buffers ahead of time against
// AudioContext.currentTime, never setTimeout-per-beat), same proven pattern
// as MetronomeEngine — but deliberately a SEPARATE implementation, not a
// reuse of MetronomeEngine, because MetronomeEngine.start() always begins
// counting its own "beat 0" from "right now." There's no way to tell it
// "start already partway through a grid anchored to some other point in
// time," which is exactly what aligning to an arbitrary file-time offset
// requires. This class was factored out of the Song Library's marker-editor
// tuning tool (MarkerEditorPlayer) so both it and live Performance-mode
// playback share one implementation instead of drifting apart.

import {
  buildSelectableStereoClickBuffer,
  createExplicitStereoPassthrough,
  createLeftOnlyRoute,
  createRightOnlyRoute,
  type StereoRoute,
} from './stereoRouting';
import type { MetronomeSettings } from '../core/types';
import { secondsPerBeat } from '../core/markerFormat';

function buildRoute(context: AudioContext, side: 'stereo' | 'left' | 'right'): StereoRoute {
  if (side === 'left') return createLeftOnlyRoute(context);
  if (side === 'right') return createRightOnlyRoute(context);
  return createExplicitStereoPassthrough(context);
}

const CLICK_SCHEDULE_AHEAD_SECONDS = 0.2;
const CLICK_SCHEDULER_INTERVAL_MS = 25;

export class SyntheticClickLayer {
  private audioContext: AudioContext;
  private gainNode: GainNode;
  private settings: MetronomeSettings;
  private offsetSeconds = 0;
  private schedulerTimer: ReturnType<typeof setInterval> | null = null;
  private scheduledSources: AudioBufferSourceNode[] = [];
  private route: StereoRoute;
  /** Which side the click buffer's own content is generated for — kept in
   * step with `route` (see updateSide) so buildSelectableStereoClickBuffer
   * writes the click into the right channel(s) for whichever route it's
   * about to be scheduled through. Only genuinely matters for 'stereo'
   * (both hard-left/hard-right routes fold BOTH input channels onto their
   * single output side regardless of which channel the buffer put the
   * click in — see createLeftOnlyRoute/createRightOnlyRoute's own doc
   * comments), but is passed through explicitly rather than relying on
   * that fold-through behavior for two of the three cases. */
  private side: 'stereo' | 'left' | 'right';
  /** The exact anchor pair "file time T lines up with AudioContext time C"
   * from the most recent start()/reanchor() — kept so currentFileTime() can
   * derive file position using this SAME anchor math the audio scheduler
   * itself uses, rather than a caller reading file position from a
   * different clock (e.g. HTMLMediaElement.currentTime) that is NOT
   * guaranteed to stay in lockstep with AudioContext.currentTime — a real,
   * commonly-documented Web Audio gotcha. Anything visualizing "where is the
   * click right now" (e.g. Performance mode's beat-flash indicator) must
   * read through here, or it will drift out of sync with the audible click
   * even though both are nominally following the same file. */
  private fileTimeAtAnchor = 0;
  private contextTimeAtAnchor = 0;
  /** The ratio between file-time seconds and real (AudioContext) seconds —
   * 1 normally, above or below 1 while PlaybackController.setPlaybackRate
   * has changed the file's practice speed. Every place below that converts
   * between the two clocks divides/multiplies by this, so the click's grid
   * (itself always expressed in FILE time, unaffected by rate) keeps
   * landing its audible buffers at the correct, correspondingly-stretched
   * REAL time — without this, the click would keep ticking at full tempo
   * while a rate-changed file drifted out of sync with it every beat.
   * Changed only via setRate(). */
  private rate = 1;

  constructor(audioContext: AudioContext, settings: MetronomeSettings, side: 'stereo' | 'left' | 'right' = 'right') {
    this.audioContext = audioContext;
    this.settings = settings;
    this.side = side;
    this.gainNode = audioContext.createGain();
    this.gainNode.gain.value = settings.clickVolume;
    this.route = buildRoute(audioContext, side);
    this.gainNode.connect(this.route.input);
    this.route.output.connect(audioContext.destination);
  }

  get isRunning(): boolean {
    return this.schedulerTimer !== null;
  }

  updateSettings(settings: MetronomeSettings): void {
    this.settings = settings;
    this.gainNode.gain.value = settings.clickVolume;
  }

  /** Changes which output the click plays on — rebuilds the routing graph
   * (splitter/merger pairs are cheap, stateless nodes) since the route is
   * fixed at construction otherwise. Safe to call whether or not the click
   * is currently running; does not affect already-scheduled buffer sources
   * (they keep playing through the OLD route until they finish — acceptable
   * since a side change is a rare settings edit, not a live-performance
   * action, and the very next scheduled beat picks up the new route). */
  updateSide(side: 'stereo' | 'left' | 'right'): void {
    this.side = side;
    this.gainNode.disconnect();
    this.route = buildRoute(this.audioContext, side);
    this.gainNode.connect(this.route.input);
    this.route.output.connect(this.audioContext.destination);
  }

  private secondsPerBeat(): number {
    return secondsPerBeat(this.settings);
  }

  /** Finds the next click-grid beat time (in file-time seconds) at or after
   * the given file-time position. The grid is anchored so a beat lands
   * exactly at `offsetSeconds` and repeats every secondsPerBeat() both
   * forward and backward from there — offset may be negative or larger than
   * one beat, so this is genuine modular arithmetic, not just
   * "offset + n*beat for n >= 0."
   *
   * Bug fix: `fileTimeSeconds` passed in here is often itself the result of
   * upstream floating-point arithmetic that's SUPPOSED to land exactly on a
   * grid beat (e.g. a count-in's anchor, computed as `playbackStartSeconds -
   * totalSeconds` where totalSeconds is itself `beats * secondsPerBeat`) —
   * but accumulated float error can land it a hair past the true beat
   * (observed: 4.000000000000001 instead of a clean 4.0). `Math.ceil` on
   * that residue rounds UP to the NEXT beat instead of the intended one,
   * silently skipping the true first beat of a count-in entirely — it's
   * never even attempted, not merely scheduled-then-skipped-as-late (which
   * has its own log line elsewhere), so nothing else in this class can
   * detect or recover from it. Snapping beatsSinceAnchor to the nearest
   * whole number whenever it's within a tiny epsilon of one (a few times
   * float64's own precision limit, nowhere near a real musical distance)
   * absorbs that residue before ceil() ever sees it, without meaningfully
   * changing behavior for any genuinely off-grid input. */
  private nextGridBeatAtOrAfter(fileTimeSeconds: number): number {
    const beat = this.secondsPerBeat();
    if (beat <= 0) return fileTimeSeconds;
    const beatsSinceAnchor = (fileTimeSeconds - this.offsetSeconds) / beat;
    const roundedBeatsSinceAnchor = Math.abs(beatsSinceAnchor - Math.round(beatsSinceAnchor)) < 1e-9 ? Math.round(beatsSinceAnchor) : beatsSinceAnchor;
    const nextBeatIndex = Math.ceil(roundedBeatsSinceAnchor);
    return this.offsetSeconds + nextBeatIndex * beat;
  }

  /** (Re)starts the click scheduler anchored to "the file is at
   * `fileTimeSeconds` right now" — call this on every play, seek, and offset
   * change while playing. A brief audible re-sync click on each restart is
   * expected/acceptable; this is not trying to smoothly retime a beat that's
   * already scheduled, just to never let the click reference a stale
   * position. */
  start(fileTimeSeconds: number, offsetSeconds: number): void {
    this.stop();
    this.offsetSeconds = offsetSeconds;
    const context = this.audioContext;

    // Anchor: "file time T lines up with audio-context time now" — every
    // future beat's absolute AudioContext.currentTime is derived from this
    // single fixed point, not accumulated tick-by-tick from playback events,
    // so it can't drift relative to the file even over a long song. Stored
    // on the instance (not just a local) so currentFileTime() can use this
    // exact same anchor — see its own doc comment for why that matters.
    this.fileTimeAtAnchor = fileTimeSeconds;
    this.contextTimeAtAnchor = context.currentTime;
    let nextBeatFileTime = this.nextGridBeatAtOrAfter(this.fileTimeAtAnchor);
    // Subdivision ticks (the "and" for 8ths, or "e"/"&"/"a" for 16ths) are
    // tracked as N-1 independent pointers — one per non-beat slot within a
    // beat — rather than a single fixed half-beat pointer, so this same
    // loop generalizes to whatever settings.subdivision currently is (1, 2,
    // or 4) instead of hardcoding "exactly one and, at the midpoint".
    // Checked fresh in tick() (not captured once here), so toggling the
    // song's subdivision setting while the click is already running takes
    // effect on the very next scheduling pass rather than requiring a
    // stop/start. Each pointer starts at nextBeatFileTime - beat*fraction
    // (that slot's position immediately BEFORE the next main beat) even
    // when that lands before the anchor — tick()'s own while loop below
    // skips anything already-passed via scheduleOne's guard and keeps
    // advancing by a full beat each pass regardless, so every pointer
    // self-corrects to its first ACTUALLY schedulable tick within its own
    // first call, without needing a separate correctness check here.
    const maxSubdivisionCount = 4; // the largest settings.subdivision currently supports (sixteenth notes)
    const subdivisionFileTimes: number[] = [];
    for (let slot = 1; slot < maxSubdivisionCount; slot++) {
      subdivisionFileTimes.push(nextBeatFileTime - (this.secondsPerBeat() * slot) / maxSubdivisionCount);
    }

    const scheduleOne = (fileTime: number, isSubdivision: boolean): void => {
      const contextTime = this.contextTimeAtAnchor + (fileTime - this.fileTimeAtAnchor) / this.rate;
      if (contextTime < context.currentTime) return; // already passed; skip rather than play late
      const buffer = buildSelectableStereoClickBuffer(context, {
        sound: this.settings.sound,
        isAccent: false,
        isSubdivision,
        sampleRate: context.sampleRate,
        gain: 1,
        channel: this.side,
      });
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.gainNode);
      source.start(contextTime);
      source.onended = () => {
        this.scheduledSources = this.scheduledSources.filter((s) => s !== source);
      };
      this.scheduledSources.push(source);
    };

    /** A backgrounded tab can throttle/pause this scheduler's own interval
     * for far longer than CLICK_SCHEDULE_AHEAD_SECONDS while
     * AudioContext.currentTime keeps advancing in real time regardless —
     * without this, the very first tick after returning would otherwise
     * need to loop through and schedule EVERY beat/subdivision missed
     * during that whole gap in one synchronous burst (potentially hundreds
     * of AudioBuffer allocations across 4 independent pointers), which is
     * exactly the kind of multi-second main-thread block that trips the
     * browser's own "Page Unresponsive" watchdog. Jumps a file-time pointer
     * forward by whole beats (staying exactly on-grid) whenever it's fallen
     * more than one beat BEHIND THE CURRENT AUDIO CLOCK ITSELF (not the
     * schedule-ahead window's far edge — a pointer legitimately sitting
     * anywhere inside that window, even close to its edge, is normal,
     * expected scheduling, not a backgrounded-tab gap), silently skipping
     * the beats in between — they were never audible anyway, since the tab
     * wasn't focused. */
    const fastForwardIfFarBehind = (pointer: number, beat: number): number => {
      const contextTimeForPointer = this.contextTimeAtAnchor + (pointer - this.fileTimeAtAnchor) / this.rate;
      const beatsBehind = Math.floor((context.currentTime - contextTimeForPointer) / (beat / this.rate));
      return beatsBehind > 1 ? pointer + beatsBehind * beat : pointer;
    };

    const tick = (): void => {
      const beat = this.secondsPerBeat();
      const scheduleAheadUntil = context.currentTime + CLICK_SCHEDULE_AHEAD_SECONDS;
      nextBeatFileTime = fastForwardIfFarBehind(nextBeatFileTime, beat);
      while (this.contextTimeAtAnchor + (nextBeatFileTime - this.fileTimeAtAnchor) / this.rate < scheduleAheadUntil) {
        scheduleOne(nextBeatFileTime, false);
        nextBeatFileTime += beat;
      }
      // Each of the (maxSubdivisionCount - 1) subdivision pointers advances
      // every pass regardless of the current setting (rather than freezing
      // while off), so re-enabling/changing the subdivision setting
      // mid-song resumes from a position still aligned to the beat grid
      // instead of scheduling a stray click wherever it happened to stop.
      // slot 2 of 4 (the exact midpoint) is the only one that plays when
      // subdivision === 2 (8th notes) — it's also the "&" when
      // subdivision === 4, matching pulseTierFor's own "1 e & a" emphasis
      // in ui/beatVisual.ts, so the audible click and the visual pulse
      // tiers agree about which tick is the "medium" one.
      for (let i = 0; i < subdivisionFileTimes.length; i++) {
        const slot = i + 1; // 1, 2, 3 out of maxSubdivisionCount (4)
        subdivisionFileTimes[i] = fastForwardIfFarBehind(subdivisionFileTimes[i], beat);
        while (this.contextTimeAtAnchor + (subdivisionFileTimes[i] - this.fileTimeAtAnchor) / this.rate < scheduleAheadUntil) {
          const shouldPlay = this.settings.subdivision === 4 || (this.settings.subdivision === 2 && slot === 2);
          if (shouldPlay) scheduleOne(subdivisionFileTimes[i], true);
          subdivisionFileTimes[i] += beat;
        }
      }
    };

    tick();
    this.schedulerTimer = setInterval(tick, CLICK_SCHEDULER_INTERVAL_MS);
  }

  /** Re-anchors an already-running click to a new file-time position (a
   * marker-tap seek or lead-in) without a caller needing to separately stop
   * then start — same effect as stop()+start() but reads as a single
   * "the file just moved" operation at call sites. No-op if not running. */
  reanchor(fileTimeSeconds: number): void {
    if (!this.isRunning) return;
    this.start(fileTimeSeconds, this.offsetSeconds);
  }

  /** Changes the file-time/real-time ratio the click's grid runs at, to
   * stay locked to a simultaneously rate-adjusted FilePlaybackEngine (see
   * PlaybackController.setPlaybackRate's own doc comment). Re-anchors at
   * the CURRENT position under the OLD rate (reading currentFileTime()
   * before changing this.rate) so playback continues from wherever it
   * genuinely is rather than jumping — same brief audible re-sync tradeoff
   * start()/reanchor() already accept elsewhere in this class. No-op while
   * not running; the next start() picks up the new rate on its own. */
  setRate(rate: number): void {
    if (this.rate === rate) return;
    const position = this.currentFileTime();
    this.rate = rate;
    if (this.isRunning) this.start(position, this.offsetSeconds);
  }

  /** The file-time position implied by the click's own scheduling anchor,
   * projected forward to right now via AudioContext.currentTime — the exact
   * same math scheduleOne()/tick() use to decide when a beat is actually
   * audible. A visual (e.g. Performance mode's beat-flash indicator) MUST
   * read file position through here rather than HTMLMediaElement.currentTime
   * if it wants to describe "what the click is doing right now" — the two
   * clocks are not guaranteed to agree, and reading the media element's own
   * time here previously caused the visual flash to drift out of sync with
   * the audible click. Returns the anchor's own fileTimeAtAnchor, unmoving,
   * while not running (nothing meaningful to project forward). */
  currentFileTime(): number {
    if (!this.isRunning) return this.fileTimeAtAnchor;
    return this.fileTimeAtAnchor + (this.audioContext.currentTime - this.contextTimeAtAnchor) * this.rate;
  }

  stop(): void {
    if (this.schedulerTimer !== null) {
      clearInterval(this.schedulerTimer);
      this.schedulerTimer = null;
    }
    for (const source of this.scheduledSources) {
      try {
        source.stop();
      } catch {
        // Already stopped; ignore.
      }
    }
    this.scheduledSources = [];
  }

  teardown(): void {
    this.stop();
    this.gainNode.disconnect();
  }
}
